import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Button from 'components/styleguide/button/Button';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./AlertDialog.module.scss');

export type AlertIconVariant = 'info' | 'success' | 'danger';

export interface AlertDialogSecondaryAction {
  prefixText?: string;
  linkText: string;
  onClick: () => void;
}

export interface AlertDialogProps {
  isOpen: boolean;
  iconName: string;
  iconVariant: AlertIconVariant;
  title: string;
  message: React.ReactNode;
  primaryText: string;
  onPrimary: () => void;
  secondary?: AlertDialogSecondaryAction;
  dismissible?: boolean;
  primaryHref?: string;
}

export default function AlertDialog({
  isOpen,
  iconName,
  iconVariant,
  title,
  message,
  primaryText,
  onPrimary,
  secondary,
  dismissible = false,
  primaryHref,
}: AlertDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleDismiss = useCallback(() => {
    if (!dismissible) return;
    onPrimary();
  }, [dismissible, onPrimary]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    handleDismiss();
  }, [handleDismiss]);

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement as HTMLElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismissible) handleDismiss();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, dismissible, handleDismiss]);

  if (!isOpen) return null;

  const titleId = 'alert-dialog-title';
  const messageId = 'alert-dialog-message';

  const badgeClasses = classNames(styles.iconBadge, styles[`iconBadge-${iconVariant}`]);

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        ref={dialogRef}
        tabIndex={-1}
      >
        {dismissible && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleDismiss}
            aria-label="Close dialog"
          >
            <Icon name="x" size="sm" color="body" aria-hidden="true" />
          </button>
        )}

        <div className={badgeClasses}>
          <Icon name={iconName} size="md" aria-hidden="true" />
        </div>

        <h3 id={titleId} className={styles.title}>{title}</h3>

        {typeof message === 'string'
          ? <p id={messageId} className={styles.message}>{message}</p>
          : <div id={messageId} className={styles.message}>{message}</div>
        }

        <div className={styles.primaryWrapper}>
          <Button
            className={styles.primaryButton}
            type="primary"
            size="S"
            context="company"
            href={primaryHref}
            target={primaryHref ? '_blank' : undefined}
            rel={primaryHref ? 'noopener noreferrer' : undefined}
            onClick={onPrimary}
          >
            {primaryText}
          </Button>
        </div>

        {secondary && (
          <p className={styles.secondary}>
            {secondary.prefixText && (
              <span className={styles.secondaryPrefix}>{secondary.prefixText} </span>
            )}
            <button
              type="button"
              className={styles.secondaryLink}
              onClick={secondary.onClick}
            >
              {secondary.linkText}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
